import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ShieldCheck, Copy, CheckCircle2 } from 'lucide-react';

const MFASetup = () => {
  const { user, tenantSlug } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [factorId, setFactorId] = useState('');
  const [qrUri, setQrUri] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/auth', { replace: true }); return; }

    (async () => {
      // Enroll a new TOTP factor (idempotent — returns existing if already enrolled)
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Masira Authenticator',
      });

      if (error || !data) {
        toast({ title: 'MFA Setup Failed', description: error?.message, variant: 'destructive' });
        setLoading(false);
        return;
      }

      setFactorId(data.id);
      setQrUri(data.totp.qr_code);
      setSecret(data.totp.secret);
      setLoading(false);
    })();
  }, [user, navigate, toast]);

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast({ title: 'Invalid Code', description: 'Enter the 6-digit code from your authenticator app.', variant: 'destructive' });
      return;
    }

    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    setVerifying(false);

    if (error) {
      toast({ title: 'Verification Failed', description: error.message, variant: 'destructive' });
      setCode('');
      return;
    }

    toast({ title: 'MFA Enabled', description: 'Your account is now protected with two-factor authentication.' });
    navigate(tenantSlug ? `/${tenantSlug}/app` : '/onboarding', { replace: true });
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="w-full max-w-md border-border/50 shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src="/masira-logo-mark.svg" alt="Masira Logo" className="h-10 w-10" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <CardTitle className="text-xl font-bold">Set Up Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Your organisation requires MFA. Scan the QR code or enter the secret manually.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* QR Code */}
          {qrUri && (
            <div className="flex flex-col items-center gap-3">
              <div className="border border-border rounded-lg p-3 bg-white">
                <img src={qrUri} alt="TOTP QR code" className="w-44 h-44" />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Scan with Google Authenticator, Authy, or any TOTP app
              </p>
            </div>
          )}

          {/* Manual entry */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Can't scan? Enter this secret manually
            </Label>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={secret}
                className="font-mono text-sm"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button variant="outline" size="icon" onClick={copySecret} title="Copy secret">
                {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <Alert>
            <AlertDescription className="text-sm">
              <ol className="list-decimal list-inside space-y-1">
                <li>Open your authenticator app</li>
                <li>Scan the QR code or enter the secret key</li>
                <li>Enter the 6-digit code shown in the app below</li>
              </ol>
            </AlertDescription>
          </Alert>

          {/* Verification */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="totp-code">Verification Code</Label>
              <Input
                id="totp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
              />
            </div>
            <Button className="w-full" onClick={handleVerify} disabled={verifying || code.length !== 6}>
              {verifying ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Verify and Enable MFA
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MFASetup;
